import { useState } from 'react'
import Navigation from '@/components/Navigation'
import { usePageMeta } from '@/hooks/usePageMeta'
import { trackEvent } from '@/lib/analytics'

const ContactPage = () => {
  usePageMeta({
    title: 'Contact | SPOKE Film',
    description: 'Reach out to the SPOKE film team with questions, partnership enquiries, or screening requests.',
  })
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    trackEvent('contact_submit', { site: 'spoke', film: 'spoke', placement: 'contact_form', provider: 'internal' })
    // Handle form submission here
    console.log('Form submitted:', formData)
    // Reset form
    setFormData({ name: '', email: '', subject: '', message: '' })
    alert('Thank you for your message! We will get back to you soon.')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <>
      <Navigation />
      <div className="pt-16">
        <section className="section">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-center mb-8 text-red-500 font-montserrat tracking-wide uppercase">Get In Touch</h1>
          
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-lg text-gray-300">
                For press inquiries, screening opportunities, or general information about SPOKE, 
                we'd love to hear from you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Contact Form */}
              <div className="card">
                <h2 className="text-xl font-semibold text-yellow-400 mb-6">Send us a message</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:border-red-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:border-red-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:border-red-500"
                    >
                      <option value="">Select a subject</option>
                      <option value="press">Press Inquiry</option>
                      <option value="screening">Screening Request</option>
                      <option value="distribution">Distribution</option>
                      <option value="general">General Information</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:border-red-500 resize-vertical"
                    />
                  </div>

                  <button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-6 rounded-lg text-lg transition-all duration-300 hover:scale-105 shadow-lg w-full">
                    Send Message
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <div className="card">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-4">Press & Media</h3>
                  <p className="text-gray-300 mb-2">
                    For press kits, high-resolution images, and interview requests.
                  </p>
                  <a href="mailto:press@spokemovie.com" className="text-red-400 hover:text-red-300">
                    press@spokemovie.com
                  </a>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-4">Distribution</h3>
                  <p className="text-gray-300 mb-2">
                    For theatrical, streaming, and licensing opportunities.
                  </p>
                  <a href="mailto:distribution@spokemovie.com" className="text-red-400 hover:text-red-300">
                    distribution@spokemovie.com
                  </a>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold text-yellow-400 mb-4">General Inquiries</h3>
                  <p className="text-gray-300 mb-2">
                    For all other questions and information.
                  </p>
                  <a href="mailto:info@spokemovie.com" className="text-red-400 hover:text-red-300">
                    info@spokemovie.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ContactPage